import React, { useEffect, useState } from 'react';
import { Star, X, Heart } from 'lucide-react';
import { Button } from './ui/button';

const STORAGE_KEY = 'apo_review_prompt_state';

/**
 * Soft review prompt — appears after 5 days of usage as a subtle bottom-right card.
 * User can dismiss (snooze 30 days), rate (opens Shopify App Store), or send feedback.
 */
export function ReviewPromptToast({ user }) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    let state;
    try {
      state = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    } catch {
      state = {};
    }
    const userState = state[user.id] || {};
    const now = Date.now();

    // First visit — record install date and don't show today
    if (!userState.first_seen_at) {
      state[user.id] = { ...userState, first_seen_at: now };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
      return;
    }
    // Already rated → never show again
    if (userState.rated) return;
    // Snoozed → check expiry
    if (userState.snoozed_until && userState.snoozed_until > now) return;

    const daysSinceFirstSeen = (now - userState.first_seen_at) / (1000 * 60 * 60 * 24);
    if (daysSinceFirstSeen >= 5) {
      // Show after 1.5s of mount so user has time to settle
      const t = setTimeout(() => setShow(true), 1500);
      return () => clearTimeout(t);
    }
  }, [user?.id]);

  if (!show || !user?.id) return null;

  const updateState = (patch) => {
    try {
      const state = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      state[user.id] = { ...(state[user.id] || {}), ...patch };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {}
  };

  const dismiss = () => {
    const SNOOZE_DAYS = 30;
    updateState({ snoozed_until: Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000 });
    setShow(false);
  };

  const rate = () => {
    updateState({ rated: true });
    setShow(false);
    window.open('https://apps.shopify.com/advanced-product-options-3', '_blank', 'noopener');
  };

  return (
    <div
      className="fixed bottom-4 right-4 z-50 w-80 max-w-[calc(100vw-2rem)] bg-card border border-border rounded-xl shadow-2xl p-4 animate-in slide-in-from-bottom-4"
      data-testid="review-prompt-toast"
    >
      <button
        type="button"
        onClick={dismiss}
        className="absolute top-2 right-2 p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground"
        aria-label="Dismiss"
        data-testid="review-prompt-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-amber-500/15 flex items-center justify-center flex-shrink-0">
          <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
        </div>
        <div className="flex-1 pr-4">
          <p className="text-sm font-semibold leading-tight">Loving Advanced Product Options?</p>
          <p className="text-xs text-muted-foreground mt-1 leading-snug">
            Help other merchants discover us with a quick review.
          </p>
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <Button size="sm" className="flex-1 bg-amber-500 hover:bg-amber-600 text-white" onClick={rate} data-testid="review-prompt-rate">
          <Star className="w-3.5 h-3.5 mr-1.5 fill-current" />
          Rate us
        </Button>
        <Button variant="outline" size="sm" onClick={dismiss} data-testid="review-prompt-later">
          Maybe later
        </Button>
      </div>
    </div>
  );
}
